import Link from 'next/link';
import Image from 'next/image';
import { SupportedLanguages } from '@/types/globals';
import { Menu } from '@/lib/fetchHeader';
import { generateLink, getLinkTarget } from '@/components/utils/getLinkTarget';
import ArrowRightIcon from '@/assets/icon/arrow-right.svg';
import { useState } from 'react';
import clsx from 'clsx';

type NavItem = {
  label: string;
  children: Menu['links'];
};

const menuText = {
  en: {
    login: 'Login',
    home: 'Home',
    back: 'Back',
  },
  fr: {
    login: 'Se Connecter',
    home: 'Accueil',
    back: 'Retour',
  },
};

export default function MobileMenu({
  lang,
  menu,
  isOpen,
  onClose,
}: {
  lang: SupportedLanguages;
  menu: Menu;
  isOpen: boolean;
  onClose: () => void;
}) {
  const ctaTarget = menu.cta ? getLinkTarget(lang, menu.cta.target) : null;
  const [mobileNavStack, setMobileNavStack] = useState<Array<NavItem>>([]);

  const navigateToSubmenu = (item: NavItem) => {
    setMobileNavStack(prev => [...prev, item]);
  };

  const closeMobileMenu = () => {
    onClose();
    setMobileNavStack([]);
  };

  const navigateBack = () => {
    setMobileNavStack(prev => prev.slice(0, -1));
  };

  const getCurrentNavItems = () => {
    if (mobileNavStack.length === 0) {
      return menu.links || [];
    }
    return mobileNavStack[mobileNavStack.length - 1].children;
  };

  return (
    <div
      className={clsx(
        'bg-qf-purple fixed right-0 left-0 h-screen overflow-y-auto shadow-lg duration-200 lg:hidden',
        isOpen
          ? 'top-20 translate-x-0 opacity-100'
          : 'top-20 translate-x-full opacity-0',
      )}
    >
      <div className="p-4">
        <div
          className={clsx(
            'font-inter border-y font-semibold',
            mobileNavStack.length > 0
              ? 'bg-dark-purple border-dark-purple -mx-4 p-4'
              : 'border-white/10',
          )}
        >
          {mobileNavStack.length > 0 ? (
            /* Back Button */
            <div className="flex items-center gap-4">
              <button
                onClick={navigateBack}
                className="text-lilac-800 flex items-center gap-2"
              >
                <ArrowRightIcon className="h-6 w-6 rotate-180" />
                {menuText[lang].back}
              </button>
            </div>
          ) : (
            /* Home Link */
            <Link
              href={lang === 'en' ? '/' : `/${lang}`}
              className="text-ultralight-purple block px-2 py-4"
              onClick={closeMobileMenu}
            >
              {menuText[lang].home}
            </Link>
          )}
        </div>

        {/* Navigation Items */}
        <div className="text-ultralight-purple">
          {getCurrentNavItems().map((link, index) => (
            <div key={link.id || index} className="border-b border-white/10">
              {link.children?.length ? (
                <button
                  onClick={() =>
                    navigateToSubmenu({
                      label: link.label,
                      children: link.children || [],
                    })
                  }
                  className="group font-inter flex w-full cursor-pointer items-center justify-between px-2 py-4 text-left font-semibold outline-none"
                >
                  {link.label}
                  <ArrowRightIcon className="h-6 w-6" />
                </button>
              ) : (
                <Link
                  href={generateLink(lang, link.type, link.slug) ?? link.slug}
                  className="group font-inter flex px-2 py-4 font-semibold"
                  onClick={closeMobileMenu}
                >
                  {link.label}
                </Link>
              )}
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="mt-8 space-y-4">
          {menu.cta && ctaTarget && (
            <Link
              href={ctaTarget}
              className="flex w-full items-center justify-center gap-2 rounded bg-teal-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-white hover:text-teal-600"
              onClick={closeMobileMenu}
            >
              {menu.cta.text}
            </Link>
          )}
          <Link
            href={process.env.NEXT_PUBLIC_APP_URL ?? '/'}
            className="hover:text-qf-purple flex w-full items-center justify-center gap-2 rounded border border-white bg-transparent px-6 py-3 font-semibold text-white transition-colors hover:bg-white"
            onClick={closeMobileMenu}
          >
            {menuText[lang].login}
          </Link>
        </div>

        {/* Featured Blocks */}
        {menu.featuredBlocks?.length > 0 && mobileNavStack.length === 0 && (
          <div className="bg-dark-purple -mx-4 mt-8 min-h-[calc(100vh-20rem)] border-t border-white/10 px-4 py-6 pt-6">
            <h3 className="font-source-serif-pro mb-4 text-xl font-semibold text-[#F5F5F5]">
              {menu.featuredBlockTitle}
            </h3>
            <div className="space-y-4">
              {menu.featuredBlocks.map(block => (
                <Link
                  key={block.id}
                  href={generateLink(block.locale, block.post_type, block.slug)}
                  className="flex gap-3 p-3 transition-colors hover:bg-white/10"
                  onClick={closeMobileMenu}
                >
                  <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg">
                    {block.image && (
                      <Image
                        src={block.image}
                        alt={block.title}
                        fill
                        className="object-cover"
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-ultralight-purple mb-1 line-clamp-2 text-sm font-semibold">
                      {block.title}
                    </h4>
                    {block.excerpt && (
                      <p className="line-clamp-2 text-sm text-[#F5F5F5]">
                        {block.excerpt.replace(/<[^>]*>/g, '')}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
